import React, { useState, useEffect } from 'react';
import { Scale, Save, Trash2, Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import { getCompanySizeWeights, setCompanySizeWeight, deleteCompanySizeWeight } from '../../api/wholesale/companySizeWeights';

/**
 * CompanySizeWeights - Configure tile weights per size category for a company
 */
const CompanySizeWeights = ({ companyId, activeSizeCategoryId = null, availableSizeCategoryIds = null }) => {
  const [sizes, setSizes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null); // Track which size is saving
  const [editValues, setEditValues] = useState({}); // Track edited values

  const fetchSizeWeights = async () => {
    try {
      setLoading(true);
      const data = await getCompanySizeWeights(companyId, true);
      setSizes(Array.isArray(data) ? data : []);
      
      // Initialize edit values with current weights
      const values = {};
      (Array.isArray(data) ? data : []).forEach(size => {
        values[size.size_category_id] = {
          weight_per_carton: size.weight_per_carton || '',
          pieces_per_carton: size.pieces_per_carton || '',
          sqm_per_carton: size.sqm_per_carton || ''
        };
      });
      setEditValues(values);
    } catch (err) {
      console.error('Error fetching size weights:', err);
      toast.error('Failed to load size weights');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyId) {
      fetchSizeWeights();
    }
  }, [companyId]);

  const handleInputChange = (sizeId, field, value) => {
    setEditValues(prev => ({
      ...prev,
      [sizeId]: {
        ...prev[sizeId],
        [field]: value
      }
    }));
  };

  const handleSave = async (sizeId) => {
    const values = editValues[sizeId];
    if (!values?.weight_per_carton || parseFloat(values.weight_per_carton) <= 0) {
      toast.error('Please enter a valid weight per carton');
      return;
    }

    setSaving(sizeId);
    try {
      await setCompanySizeWeight(companyId, sizeId, {
        weight_per_carton: parseFloat(values.weight_per_carton),
        pieces_per_carton: values.pieces_per_carton ? parseInt(values.pieces_per_carton) : null,
        sqm_per_carton: values.sqm_per_carton ? parseFloat(values.sqm_per_carton) : null
      });
      toast.success('Weight saved successfully');
      fetchSizeWeights();
    } catch (err) {
      console.error('Error saving weight:', err);
      toast.error(err.message || 'Failed to save weight');
    } finally {
      setSaving(null);
    }
  };

  const handleDelete = async (sizeId, sizeName) => {
    if (!window.confirm(`Remove weight configuration for ${sizeName}?`)) {
      return;
    }

    setSaving(sizeId);
    try {
      await deleteCompanySizeWeight(companyId, sizeId);
      toast.success('Weight configuration removed');
      fetchSizeWeights();
    } catch (err) {
      console.error('Error deleting weight:', err);
      toast.error(err.message || 'Failed to delete weight');
    } finally {
      setSaving(null);
    }
  };

  const hasChanges = (sizeId) => {
    const current = sizes.find(s => s.size_category_id === sizeId);
    const edited = editValues[sizeId];
    if (!current || !edited) return false;

    return (
      (edited.weight_per_carton || '') !== (current.weight_per_carton?.toString() || '') ||
      (edited.pieces_per_carton || '') !== (current.pieces_per_carton?.toString() || '') ||
      (edited.sqm_per_carton || '') !== (current.sqm_per_carton?.toString() || '')
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-emerald-500 border-t-transparent"></div>
      </div>
    );
  }

  if (sizes.length === 0) {
    return (
      <div className="text-center py-8">
        <AlertCircle className="w-12 h-12 mx-auto text-gray-300 mb-3" />
        <p className="text-gray-500">No size categories available</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-4 px-6 text-sm font-semibold text-gray-900">Size</th>
              <th className="text-left py-4 px-6 text-sm font-semibold text-gray-900">Weight/Carton (kg)</th>
              <th className="text-center py-4 px-6 text-sm font-semibold text-gray-900">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {sizes
              .filter(size => {
                if (!availableSizeCategoryIds) return true;
                return availableSizeCategoryIds.includes(size.size_category_id);
              })
              .map((size) => {
              const sizeId = size.size_category_id;
              const isConfigured = size.weight_id !== null && size.weight_per_carton !== null;
              const isSaving = saving === sizeId;
              const changed = hasChanges(sizeId);
              
              // Check if row should be active based on passed activeSizeCategoryId selection
              const isActive = !activeSizeCategoryId || activeSizeCategoryId === sizeId;

              return (
                <tr 
                  key={sizeId} 
                  className={`
                    ${isConfigured ? 'bg-green-50/50' : 'bg-white'} 
                    ${isActive ? 'hover:bg-gray-50' : 'opacity-40 cursor-not-allowed bg-gray-50'}
                    transition-colors
                  `}
                >
                  <td className="py-4 px-6">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">{size.size_category_name}</span>
                      {isConfigured && (
                        <span className="w-2 h-2 bg-green-500 rounded-full" title="Weight configured" />
                      )}
                    </div>
                  </td>
                  <td className="py-4 px-6">
                    <input
                      type="number"
                      step="0.001"
                      min="0"
                      value={editValues[sizeId]?.weight_per_carton || ''}
                      onChange={(e) => handleInputChange(sizeId, 'weight_per_carton', e.target.value)}
                      placeholder="0.000"
                      disabled={!isActive}
                      className="w-32 px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm disabled:cursor-not-allowed disabled:bg-gray-100"
                    />
                  </td>
                  <td className="py-4 px-6">
                    <div className="flex items-center justify-center gap-4">
                      <button
                        onClick={() => handleSave(sizeId)}
                        disabled={!isActive || isSaving || !editValues[sizeId]?.weight_per_carton}
                        className={`p-2 rounded-lg transition-colors ${
                          changed 
                            ? 'bg-emerald-600 text-white hover:bg-emerald-700' 
                            : 'bg-gray-100 text-gray-400'
                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                        title="Save weight"
                      >
                        {isSaving ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Save className="w-4 h-4" />
                        )}
                      </button>
                      {isConfigured && (
                        <button
                          onClick={() => handleDelete(sizeId, size.size_category_name)}
                          disabled={!isActive || isSaving}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Remove weight"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      
      <p className="text-xs text-gray-400 mt-2">
        Configure weight per carton for each size category. This is used to calculate tonnage during orders.
      </p>
    </div>
  );
};

export default CompanySizeWeights;
